import { ChevronIcon } from 'components/icons'
import classNames from 'helpers/class-names'
import { FC, useEffect } from 'react'

interface PaginationMetadata {
  totalPages: number
  perPage: number
}

interface Props {
  pagination: PaginationMetadata
  page: number
  onClick: (page: number) => void
  scrollToTop?: boolean
}

export const Pagination: FC<Props> = ({
  pagination,
  page,
  onClick,
  scrollToTop = false,
}) => {
  const isDisabled = pagination.totalPages <= 1

  const paginationLink = (linkedPage: number, ellipses: boolean = false) => (
    <li className="inline-block text-xs tracking-tight text-gray-300">
      <button
        disabled={page === linkedPage || isDisabled}
        className={classNames(
          'select-none rounded-sm hover:bg-gray-50 hover:text-gray',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
          'font-bold transition-colors duration-200',
          {
            'bg-gray-300 text-white hover:bg-gray-300 hover:text-white':
              page === linkedPage && !isDisabled,
            'hidden sm:block': page !== linkedPage,
            'bg-gray-100 text-white hover:bg-gray-100 hover:text-white':
              isDisabled,
          },
        )}
        onClick={() => onClick(linkedPage)}>
        <div className="min-w-4 px-1 py-px">{ellipses ? '…' : linkedPage}</div>
      </button>
    </li>
  )

  useEffect(() => {
    if (scrollToTop && typeof window !== 'undefined') {
      window.scrollTo(0, 0)
    }
  }, [page])

  return (
    <div className="flex whitespace-nowrap">
      <button
        disabled={page === 1}
        className={classNames(
          'mt-px flex items-center rounded-sm font-semibold text-gray-300 hover:bg-gray-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
          {
            'cursor-not-allowed text-gray-100 hover:bg-transparent': page === 1,
          },
        )}
        onClick={() => onClick(page - 1)}>
        <ChevronIcon className="size-5 rotate-180" />
        <span className="sr-only">Previous</span>
      </button>
      <nav className="text-sm text-gray-400">
        <ul>
          {paginationLink(1)}
          {pagination.totalPages >= 2 &&
            (pagination.totalPages <= 5 || page < 4) &&
            paginationLink(2)}
          {pagination.totalPages >= 3 &&
            (pagination.totalPages <= 5 || page < 4) &&
            paginationLink(3)}
          {pagination.totalPages === 5 && paginationLink(4)}
          {page > 3 &&
            pagination.totalPages > 5 &&
            paginationLink(
              page >= pagination.totalPages - 2
                ? pagination.totalPages - 3
                : page - 2,
              true,
            )}
          {pagination.totalPages > 6 &&
            page >= 4 &&
            page < pagination.totalPages - 2 &&
            paginationLink(page - 1)}
          {pagination.totalPages > 6 &&
            page >= 4 &&
            page < pagination.totalPages - 2 &&
            paginationLink(page)}
          {pagination.totalPages > 6 &&
            page >= 4 &&
            page < pagination.totalPages - 2 &&
            paginationLink(page + 1)}

          {pagination.totalPages > 5 &&
            page < pagination.totalPages - 2 &&
            paginationLink(
              page < 3
                ? 4
                : page === 3
                ? page + 1
                : page <= pagination.totalPages - 3
                ? page + 2
                : pagination.totalPages - 4,
              true,
            )}

          {pagination.totalPages > 5 &&
            page >= pagination.totalPages - 2 &&
            paginationLink(pagination.totalPages - 2)}
          {pagination.totalPages > 5 &&
            page >= pagination.totalPages - 2 &&
            paginationLink(pagination.totalPages - 1)}
          {pagination.totalPages > 3 && paginationLink(pagination.totalPages)}
        </ul>
      </nav>
      <button
        disabled={page >= pagination.totalPages}
        className={classNames(
          'mt-px flex items-center rounded-sm font-semibold text-gray-300 hover:bg-gray-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
          {
            'cursor-not-allowed text-gray-100 hover:bg-transparent':
              page >= pagination.totalPages,
          },
        )}
        onClick={() => onClick(page + 1)}>
        <span className="sr-only">Next</span>
        <ChevronIcon className="size-5" />
      </button>
    </div>
  )
}
